import { useEffect, useMemo, useState } from 'react';
import { Modal, Input, Button, Tooltip, message } from 'antd';
import {
  DeleteOutlined,
  HolderOutlined,
  PlusOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FilterEditor } from '../../shared/table/FilterBar';
import { getOperatorsForFieldType } from '../../shared/components/FilterRow';
import { describeFilters } from '../../shared/table/filterSummary';
import type { QuickFilter } from '../../shared/table/quickFilters';
import type { FieldLike, FilterLeaf, ReferenceOption } from '../../shared/table/types';

export interface QuickFiltersManagerModalProps {
  open: boolean;
  onClose: () => void;
  fields: FieldLike[];
  quickFilters: QuickFilter[];
  onSave: (next: QuickFilter[]) => Promise<void>;
  ensureReferenceOptions?: (field: FieldLike) => Promise<ReferenceOption[]>;
  resolvedRefs?: Record<string, string>;
  /** Текущие пользовательские фильтры — для кнопки «Из текущего фильтра». */
  currentUserFilters?: FilterLeaf[];
}

function newId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `qf-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
  }
}

function blankLeaf(fields: FieldLike[], existing: number): FilterLeaf {
  const first = fields[0];
  const ops = first ? getOperatorsForFieldType(first.fieldType) : ['eq'];
  return {
    fieldSlug: first?.slug ?? '',
    operator: ops[0] ?? 'eq',
    value: undefined,
    ...(existing > 0 ? { combinator: 'and' as const } : {}),
  };
}

interface ListRowProps {
  qf: QuickFilter;
  summary: string;
  selected: boolean;
  onSelect: () => void;
  onRemove: () => void;
}

/** Строка списка слева: название + сводка условий, перетаскивается за ручку. */
function ListRow({ qf, summary, selected, onSelect, onRemove }: ListRowProps): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: qf.id,
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
      }}
      className={`lens-qf-row${selected ? ' lens-qf-row--selected' : ''}`}
      onClick={onSelect}
      data-testid={`quick-filter-row-${qf.id}`}
    >
      <span className="lens-qf-row__handle" {...attributes} {...listeners} aria-label="Переместить">
        <HolderOutlined />
      </span>
      <span className="lens-qf-row__body">
        <span className="lens-qf-row__name">{qf.name || 'Без названия'}</span>
        <span className="lens-qf-row__summary">{summary || 'Без условий'}</span>
      </span>
      <Button
        type="text"
        size="small"
        danger
        icon={<DeleteOutlined />}
        className="lens-qf-row__remove"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        aria-label="Удалить быстрый фильтр"
      />
    </div>
  );
}

/**
 * Управление быстрыми фильтрами представления (admin-only). Слева список
 * (название + сводка условий, порядок перетаскиванием), справа редактор
 * выбранного. Правит копию списка, сохранение целиком уходит в
 * `view.config.quickFilters` через onSave.
 */
export function QuickFiltersManagerModal({
  open,
  onClose,
  fields,
  quickFilters,
  onSave,
  ensureReferenceOptions,
  resolvedRefs,
  currentUserFilters,
}: QuickFiltersManagerModalProps): JSX.Element {
  const [draft, setDraft] = useState<QuickFilter[]>(quickFilters);
  const [selectedId, setSelectedId] = useState<string | null>(quickFilters[0]?.id ?? null);
  const [saving, setSaving] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    if (open) {
      setDraft(quickFilters);
      setSelectedId(quickFilters[0]?.id ?? null);
    }
  }, [open, quickFilters]);

  const selected = draft.find((q) => q.id === selectedId) ?? null;

  const summaries = useMemo(
    () => Object.fromEntries(draft.map((q) => [q.id, describeFilters(q.filters, fields, resolvedRefs)])),
    [draft, fields, resolvedRefs],
  );

  const patch = (id: string, patchObj: Partial<QuickFilter>) =>
    setDraft((prev) => prev.map((q) => (q.id === id ? { ...q, ...patchObj } : q)));

  const setFilters = (id: string, filters: FilterLeaf[]) => patch(id, { filters });

  const addQuick = (filters: FilterLeaf[]) => {
    const id = newId();
    setDraft((prev) => [...prev, { id, name: `Фильтр ${prev.length + 1}`, filters }]);
    setSelectedId(id);
  };

  const remove = (id: string) =>
    setDraft((prev) => {
      const next = prev.filter((q) => q.id !== id);
      if (id === selectedId) setSelectedId(next[0]?.id ?? null);
      return next;
    });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDraft((prev) => {
      const from = prev.findIndex((q) => q.id === active.id);
      const to = prev.findIndex((q) => q.id === over.id);
      if (from < 0 || to < 0) return prev;
      return arrayMove(prev, from, to);
    });
  };

  const handleSave = async () => {
    const cleaned = draft.map((q) => ({ ...q, name: q.name.trim() }));
    if (cleaned.some((q) => q.name.length === 0)) {
      void message.error('У быстрого фильтра должно быть название');
      return;
    }
    setSaving(true);
    try {
      await onSave(cleaned);
      onClose();
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Не удалось сохранить быстрые фильтры');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <span>
          <ThunderboltOutlined style={{ marginRight: 8 }} />
          Быстрые фильтры представления
        </span>
      }
      width={1040}
      okText="Сохранить"
      cancelText="Отмена"
      confirmLoading={saving}
      onOk={handleSave}
      destroyOnClose
    >
      <div className="lens-qf-manager">
        <div className="lens-qf-manager__list">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={draft.map((q) => q.id)} strategy={verticalListSortingStrategy}>
              <div className="lens-qf-manager__rows">
                {draft.map((qf) => (
                  <ListRow
                    key={qf.id}
                    qf={qf}
                    summary={summaries[qf.id] ?? ''}
                    selected={qf.id === selectedId}
                    onSelect={() => setSelectedId(qf.id)}
                    onRemove={() => remove(qf.id)}
                  />
                ))}
                {draft.length === 0 && (
                  <div className="lens-qf-manager__empty">Быстрых фильтров ещё нет</div>
                )}
              </div>
            </SortableContext>
          </DndContext>

          <div className="lens-qf-manager__list-actions">
            <Button size="small" type="text" icon={<PlusOutlined />} onClick={() => addQuick([])}>
              Добавить
            </Button>
            {currentUserFilters && currentUserFilters.length > 0 && (
              <Tooltip title="Создать быстрый фильтр из условий, применённых сейчас в таблице">
                <Button
                  size="small"
                  type="text"
                  onClick={() => addQuick(currentUserFilters.map((f) => ({ ...f })))}
                >
                  Из текущего фильтра
                </Button>
              </Tooltip>
            )}
          </div>
        </div>

        <div className="lens-qf-manager__editor">
          {selected ? (
            <>
              <Input
                value={selected.name}
                onChange={(e) => patch(selected.id, { name: e.target.value })}
                placeholder="Название быстрого фильтра"
                maxLength={100}
                data-testid={`quick-filter-name-${selected.id}`}
              />
              <div
                className="lens-qf-manager__conditions lens-filter-editor lens-filter-editor--flat"
                data-testid={`quick-filter-editor-${selected.id}`}
              >
                <div className="lens-qf-manager__conditions-head">
                  <span>Условия</span>
                  {selected.filters.length > 0 && (
                    <Button
                      size="small"
                      type="text"
                      danger
                      onClick={() => setFilters(selected.id, [])}
                    >
                      Очистить
                    </Button>
                  )}
                </div>
                <FilterEditor
                  compact
                  fields={fields}
                  filters={selected.filters}
                  onUpdate={(idx, p) =>
                    setFilters(
                      selected.id,
                      selected.filters.map((f, i) => (i === idx ? { ...f, ...p } : f)),
                    )
                  }
                  onRemove={(idx) =>
                    setFilters(
                      selected.id,
                      selected.filters.filter((_, i) => i !== idx),
                    )
                  }
                  onAdd={() =>
                    setFilters(selected.id, [
                      ...selected.filters,
                      blankLeaf(fields, selected.filters.length),
                    ])
                  }
                  onClear={() => setFilters(selected.id, [])}
                  ensureReferenceOptions={ensureReferenceOptions}
                  resolvedRefs={resolvedRefs}
                />
              </div>
              <p className="lens-qf-manager__hint">
                Быстрые фильтры общие для всех в этом представлении. Каждый — чип-переключатель над
                таблицей; включённые комбинируются по «И».
              </p>
            </>
          ) : (
            <div className="lens-qf-manager__placeholder">
              Выберите быстрый фильтр слева или добавьте новый
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
